import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "../api";

import { toasts } from "../components/AppToast";
import Pagination from "../components/Pagination";
import Layout from "../components/Layout";

import {
  FiTrash2,
  FiEdit,
  FiPlus,
  FiSearch,
} from "react-icons/fi";

export default function CarGroup() {
  const navigate = useNavigate();

  // =========================================================
  // USER
  // =========================================================

  const [user, setUser] = useState(null);

  // =========================================================
  // TABLE
  // =========================================================

  const [data, setData] = useState([]);

  const [page, setPage] = useState(1);

  const [totalPages, setTotalPages] = useState(1);

  const [total, setTotal] = useState(0);

  const [search, setSearch] = useState("");

  const [loading, setLoading] = useState(false);

  // =========================================================
  // FORM
  // =========================================================

  const [name, setName] = useState("");

  const [editId, setEditId] = useState(null);

  const [saving, setSaving] = useState(false);

  // =========================================================
  // DELETE MODAL
  // =========================================================

  const [delModal, setDelModal] = useState(null);

  const [delSaving, setDelSaving] = useState(false);

  // =========================================================
  // AUTH
  // =========================================================

  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!token) {
      navigate("/login");
      return;
    }

    try {
      const storedUser = JSON.parse(
        localStorage.getItem("user") || "null"
      );

      setUser(storedUser);
    } catch (error) {
      console.error(
        "User parse error:",
        error
      );

      setUser(null);
    }
  }, [navigate]);

  // =========================================================
  // FETCH DATA
  // =========================================================

  const fetchData = async (pg, q) => {
    try {
      setLoading(true);

      const res = await API.get("/cargroup", {
        params: {
          page: pg,
          limit: 10,
          search: q || "",
        },
      });

      console.log(
        "CAR GROUP PAGINATION:",
        res.data
      );

      setData(
        Array.isArray(res.data?.data)
          ? res.data.data
          : []
      );

      setTotal(
        Number(res.data?.total || 0)
      );

      setTotalPages(
        Number(res.data?.totalPages || 1)
      );
    } catch (error) {
      console.error(
        "Fetch Car Group error:",
        error
      );

      toasts.error(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to load Car Groups"
      );

      setData([]);
      setTotal(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // LOAD TABLE
  //
  // IMPORTANT:
  // Only ONE useEffect for pagination/search.
  // =========================================================

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchData(page, search);
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [page, search]);

  // =========================================================
  // SEARCH
  // =========================================================

  const handleSearchChange = (e) => {
    const value = e.target.value;

    setSearch(value);

    // Always start search from page 1
    setPage(1);
  };

  // =========================================================
  // CLEAR SEARCH
  // =========================================================

  const handleClearSearch = () => {
    setSearch("");

    setPage(1);
  };

  // =========================================================
  // ADD / UPDATE
  // =========================================================

  const handleSubmit = async () => {
    if (!name.trim()) {
      toasts.warning(
        "Enter Car Group name"
      );

      return;
    }

    if (!user?.user_id) {
      toasts.warning(
        "Something is Wrong"
      );

      localStorage.clear();

      navigate("/login");

      return;
    }

    setSaving(true);

    try {
      if (editId) {
        await API.put(
          `/cargroup/${editId}`,
          {
            name: name.trim(),
            user_id: user.user_id,
          }
        );

        toasts.success(
          "Car Group updated successfully"
        );
      } else {
        await API.post(
          "/cargroup",
          {
            name: name.trim(),
            user_id: user.user_id,
          }
        );

        toasts.success(
          "Car Group added successfully"
        );
      }

      setName("");

      setEditId(null);

      // Reload current page
      await fetchData(
        page,
        search
      );
    } catch (error) {
      console.error(
        "Submit error:",
        error
      );

      toasts.error(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Operation failed"
      );
    } finally {
      setSaving(false);
    }
  };

  // =========================================================
  // EDIT
  // =========================================================

  const handleEdit = (row) => {
    setName(
      row.cat_name || ""
    );

    setEditId(
      row.cat_id
    );

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // =========================================================
  // CANCEL EDIT
  // =========================================================

  const handleCancel = () => {
    setName("");

    setEditId(null);
  };

  // =========================================================
  // OPEN DELETE MODAL
  // =========================================================

  const openDeleteModal = (row) => {
    setDelModal(row);
  };

  // =========================================================
  // CLOSE DELETE MODAL
  // =========================================================

  const closeDeleteModal = () => {
    if (delSaving) {
      return;
    }

    setDelModal(null);
  };

  // =========================================================
  // DELETE
  // =========================================================

  const handleDelete = async () => {
    if (!delModal?.cat_id) {
      return;
    }

    setDelSaving(true);

    try {
      await API.delete(
        `/cargroup/${delModal.cat_id}`
      );

      toasts.success(
        "Car Group deleted successfully"
      );

      const deletedId =
        delModal.cat_id;

      setDelModal(null);

      if (
        editId === deletedId
      ) {
        setName("");

        setEditId(null);
      }

      // -----------------------------------------------------
      // Calculate remaining records
      // -----------------------------------------------------

      const remainingRecords =
        Math.max(
          0,
          total - 1
        );

      const newTotalPages =
        Math.max(
          1,
          Math.ceil(
            remainingRecords / 10
          )
        );

      // -----------------------------------------------------
      // If current page becomes invalid,
      // go to previous page.
      // -----------------------------------------------------

      if (
        page > newTotalPages
      ) {
        setPage(
          newTotalPages
        );

        return;
      }

      // -----------------------------------------------------
      // Otherwise reload current page.
      // -----------------------------------------------------

      await fetchData(
        page,
        search
      );
    } catch (error) {
      console.error(
        "Delete error:",
        error
      );

      toasts.error(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Delete failed"
      );
    } finally {
      setDelSaving(false);
    }
  };

  // =========================================================
  // PAGINATION - PREVIOUS
  // =========================================================

  const handlePrevious = () => {
    if (
      loading ||
      page <= 1
    ) {
      return;
    }

    setPage(
      (prev) =>
        Math.max(
          1,
          prev - 1
        )
    );
  };

  // =========================================================
  // PAGINATION - NEXT
  // =========================================================

  const handleNext = () => {
    if (
      loading ||
      totalPages <= 0 ||
      page >= totalPages
    ) {
      return;
    }

    setPage(
      (prev) =>
        Math.min(
          totalPages,
          prev + 1
        )
    );
  };

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <Layout activePage="Car Group">

      <main className="dashboard-main">

        {/* ===================================================
            CARD
        =================================================== */}

        <div className="season-card">

          {/* =================================================
              TOP ROW
          ================================================= */}

          <div className="season-top-row">

            {/* =================================================
                FORM
            ================================================= */}

            <div className="season-form-row">

              {editId && (
                <span className="season-editing-badge">
                  Editing #{editId}
                </span>
              )}

              <input
                type="text"
                placeholder="Car Group Name"
                value={name}
                onChange={(e) =>
                  setName(
                    e.target.value
                  )
                }
                onKeyDown={(e) => {
                  if (
                    e.key ===
                      "Enter" &&
                    !saving
                  ) {
                    handleSubmit();
                  }
                }}
                className="season-input"
              />

              <input
                type="hidden"
                value={
                  user?.user_id || ""
                }
                readOnly
              />

              {editId && (
                <button
                  type="button"
                  onClick={
                    handleCancel
                  }
                  className="season-cancel-btn"
                  disabled={saving}
                >
                  Cancel
                </button>
              )}

              <button
                type="button"
                onClick={
                  handleSubmit
                }
                className="um-btn-primary"
                disabled={saving}
              >
                {saving ? (
                  <span className="spinner" />
                ) : editId ? (
                  <>
                    <FiEdit />
                    Update Car Group
                  </>
                ) : (
                  <>
                    <FiPlus />
                    Add Car Group
                  </>
                )}
              </button>

            </div>

            {/* =================================================
                SEARCH
            ================================================= */}

            <div className="search-wrapper">

              <span className="search-icon">
                <FiSearch size={17} />
              </span>

              <input
                className="search-input"
                type="text"
                placeholder="Search Car Group..."
                value={search}
                onChange={
                  handleSearchChange
                }
              />

              {search && (
                <button
                  type="button"
                  className="season-clear-btn"
                  onClick={
                    handleClearSearch
                  }
                >
                  ✕
                </button>
              )}

            </div>

          </div>

          {/* =================================================
              TABLE
          ================================================= */}

          <div className="table-responsive">

            <table className="table table-striped table-hover table-bordered align-middle mb-0">

              <thead className="table-light">

                <tr className="um-table-head">

                  <th className="um-th text-nowrap">
                    #
                  </th>

                  <th className="um-th text-nowrap text-center">
                    Group Name
                  </th>

                  <th className="um-th text-nowrap text-center">
                    Action
                  </th>

                </tr>

              </thead>

              <tbody>

                {/* =================================================
                    LOADING
                ================================================= */}

                {loading ? (

                  <tr>

                    <td
                      colSpan={3}
                      className="um-td text-center py-4"
                    >
                      Loading…
                    </td>

                  </tr>

                ) : data.length === 0 ? (

                  /* =================================================
                      EMPTY
                  ================================================= */

                  <tr>

                    <td
                      colSpan={3}
                      className="empty-table-cell text-center py-5"
                    >

                      {search
                        ? "No matching Car Groups found"
                        : "No Car Groups found"}

                    </td>

                  </tr>

                ) : (

                  /* =================================================
                      DATA
                  ================================================= */

                  data.map(
                    (row, i) => (

                      <tr
                        key={
                          row.cat_id
                        }
                        className={`um-row ${
                          editId ===
                          row.cat_id
                            ? "editing-row"
                            : ""
                        }`}
                      >

                        {/* NUMBER */}

                        <td className="um-td user-number text-center">

                          {(page - 1) *
                            10 +
                            i +
                            1}

                        </td>

                        {/* GROUP NAME */}

                        <td className="um-td text-center">

                          <span className="season-name-chip">

                            {row.cat_name ||
                              "—"}

                          </span>

                        </td>

                        {/* ACTION */}

                        <td className="um-td text-center season-action-td">

                          <div className="action-buttons d-flex justify-content-center align-items-center gap-2">

                            {/* EDIT */}

                            <button
                              type="button"
                              onClick={() =>
                                handleEdit(
                                  row
                                )
                              }
                              className="action-btn action-edit btn btn-sm"
                              title="Edit"
                              disabled={
                                loading ||
                                delSaving
                              }
                            >
                              <FiEdit
                                size={15}
                              />
                            </button>

                            {/* DELETE */}

                            <button
                              type="button"
                              onClick={() =>
                                openDeleteModal(
                                  row
                                )
                              }
                              className="action-btn action-delete btn btn-sm"
                              title="Delete"
                              disabled={
                                loading ||
                                delSaving
                              }
                            >
                              <FiTrash2
                                size={15}
                              />
                            </button>

                          </div>

                        </td>

                      </tr>

                    )
                  )

                )}

              </tbody>

            </table>

            {/* =================================================
                PAGINATION
            ================================================= */}

            <Pagination
              page={page}
              totalPages={totalPages}
              total={total}
              limit={10}
              loading={loading}
              onPrevious={handlePrevious}
              onNext={handleNext}
            />

          </div>

        </div>

      </main>

      {/* =====================================================
          DELETE MODAL
      ====================================================== */}

      {delModal && (

        <div
          className="um-overlay"
          onClick={
            closeDeleteModal
          }
        >

          <div
            className="um-modal um-delete-modal"
            onClick={(e) =>
              e.stopPropagation()
            }
          >

            <div className="delete-content">

              {/* ICON */}

              <div className="delete-icon">

                <FiTrash2
                  size={32}
                />

              </div>

              {/* TITLE */}

              <h2 className="delete-title">

                Delete Car Group?

              </h2>

              {/* DESCRIPTION */}

              <p className="delete-description">

                Are you sure you want
                to delete{" "}

                <span className="badge badge-success">

                  "{delModal.cat_name}"

                </span>

                ?

                <br />

                This will permanently
                remove this Car Group.

                <br />

                <strong>
                  This action cannot be
                  undone.
                </strong>

              </p>

              {/* ACTIONS */}

              <div className="delete-actions">

                {/* CANCEL */}

                <button
                  type="button"
                  className="um-btn-secondary"
                  onClick={
                    closeDeleteModal
                  }
                  disabled={
                    delSaving
                  }
                >
                  Cancel
                </button>

                {/* DELETE */}

                <button
                  type="button"
                  disabled={
                    delSaving
                  }
                  className="delete-confirm-btn"
                  onClick={
                    handleDelete
                  }
                >

                  {delSaving ? (

                    <span className="spinner" />

                  ) : (

                    <>
                      <FiTrash2 />
                      Delete Permanently
                    </>

                  )}

                </button>

              </div>

            </div>

          </div>

        </div>

      )}

      {/* =====================================================
          TOAST
      ====================================================== */}

     

    </Layout>
  );
}
